import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  SFContentContainer,
  SFCard,
  SFHeroCard,
  SFProfileTile,
  SFGridContainer,
  SFGridItem,
  SFButtonGroup,
  SFClientSideFilterActions,
} from "@sf-core";
import { LPUrls } from "@sf-launchpad";
import { useNavigate, useLocation, Routes, Route } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import backgroundImage from "./images/dashboard_bg_banner.png";
import _ from "lodash";
import {
  getMySkills,
  getJobsOfInterest,
  getTimeInvestment,
} from "../../services/dashboardService";
import { getUserJobRole } from "../../services/profileService";
import MySkills from "./screens/mySkills";
import SkillComparison from "./screens/skillComparison";
import TimeInvestment from "./screens/timeInvestment";
import JobsOfInterest from "./components/jobsOfInterest";
import MySkillsCard from "./components/mySkillsCard";
import BelowTargetCard from "./components/belowTargetCard";
import RecentlyProgressedCard from "./components/recentlyProgressedCard";
import ProgressDialogs from "./components/progressDialogs";
import { profileActions } from "../../redux/reducers/profileReducer";
import { getProfileUrl, getJobUrl } from "../../constants/urls";
import { PROFILE_STEPS } from "../../constants/profileSteps";
import { GRID_NAMES } from "../../constants/gridNames";
import { PROGRESS_TYPES } from "../../constants/progressTypes";
import { PATHS } from "../../constants/paths";

const MyProgressScreen = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [userJobRole, setUserJobRole] = useState({});
  const userJobRoleId = useSelector(
    (state) => state.userConfiguration.jobRoleId
  );
  const userAvatar = useSelector((state) => state.session.avatar);

  const [skills, setSkills] = useState([]);
  const [jobsOfInterest, setJobsOfInterest] = useState({ data: [], loading: true });
  const [timeInvestment, setTimeInvestment] = useState([]);

  const [dialog, setDialog] = useState({ jobId: null, open: false });

  const jobMatchPercent = useMemo(() => {
    if (skills.length == 0) return 0;

    let totalSkills = _.filter(skills, (skill) => skill.isJobRoleSkill).length;
    let totalMatched = _.filter(
      skills,
      (skill) =>
        skill.proficiencyLevel >= skill.proficiencyLevelTarget &&
        skill.isJobRoleSkill
    ).length;
    return Math.round((totalMatched / totalSkills) * 100);
  }, [skills]);

  const skillActions = [
    {
      label: t("dashboardScreen.manageSkills"),
      onClick: () => {
        dispatch(
          profileActions.setState({
            activeStep: PROFILE_STEPS.SKILLS,
          })
        );
        navigate(getProfileUrl());
      },
    },
    {
      label: t("dashboardScreen.clearFilters"),
      onClick: () => clearFilters(),
    },
  ];

  // On step load
  useEffect(() => {
    getMySkills().then((res) => {
      setSkills(res.data);
    });
    getJobsOfInterest().then((res) => {
      setJobsOfInterest({ data: res.data, loading: false });
    });
    getTimeInvestment().then((res) => {
      setTimeInvestment(res.data);
    });
    getUserJobRole().then((res) => {
      setUserJobRole({ id: res.data.id, name: res.data.name });
    });
  }, []);

  // eslint-disable-next-line no-unused-vars
  const TAB_PATHS = [
    {
      id: LPUrls.getMyProgressUrl("jobs-and-skills"),
      label: t("dashboardScreen.jobsAndSkillsSection"),
    },
    {
      id: LPUrls.getMyProgressUrl("learning"),
      label: t("dashboardScreen.learningSection"),
    },
  ];

  const clearFilters = () => {
    const payload = {
      gridName: GRID_NAMES.DASHBOARD_MY_SKILLS,
      state: {
        filter: {
          filterModel: {},
        },
      },
    };

    dispatch(SFClientSideFilterActions.saveState(payload));
  };

  return (
    <>
      {dialog?.open && (
        <ProgressDialogs dialog={dialog} setDialog={setDialog} />
      )}
      <SFContentContainer
        backgroundImage={backgroundImage}
        backgroundImageProps={{
          backgroundPosition: "top",
          backgroundSize: "contain",
        }}
      >
        <Routes>
          <Route
            path={PATHS.JOB_SKILLS}
            element={
              <>
                <SFGridContainer sx={{ mb: 3, mt: 1 }}>
                  <SFGridItem xs={12} sm={6} md={6} lg={3}>
                    <SFHeroCard
                      actions={[
                        {
                          label: t("dashboardScreen.viewJob"),
                          onClick: () => {
                            navigate(getJobUrl(userJobRoleId));
                          },
                        },
                        {
                          label: t("dashboardScreen.manageJob"),
                          onClick: () => {
                            dispatch(
                              profileActions.setState({
                                activeStep: PROFILE_STEPS.JOB_ROLE,
                              })
                            );
                            navigate(getProfileUrl());
                          },
                        },
                      ]}
                    >
                      <SFProfileTile
                        title={userJobRole.name}
                        subTitle={t("dashboardScreen.myJobSelection")}
                        avatar={userAvatar}
                        progress={jobMatchPercent}
                        onClick={() => {
                          setDialog({
                            jobId: userJobRole.id,
                            open: true,
                            type: PROGRESS_TYPES.JOB_ROLE,
                          });
                        }}
                      />
                    </SFHeroCard>
                  </SFGridItem>
                  <SFGridItem xs={12} sm={6} md={6} lg={3}>
                    <MySkillsCard skills={skills} actions={skillActions} />
                  </SFGridItem>
                  <SFGridItem xs={12} sm={6} md={6} lg={3}>
                    <RecentlyProgressedCard
                      skills={skills}
                      actions={skillActions}
                    />
                  </SFGridItem>
                  <SFGridItem xs={12} sm={6} md={6} lg={3}>
                    <BelowTargetCard skills={skills} actions={skillActions} />
                  </SFGridItem>
                </SFGridContainer>
                <SFGridContainer>
                  <SFGridItem xs={12} sm={12} md={8}>
                    <SFCard
                      actions={[
                        {
                          label: t("dashboardScreen.clearFilters"),
                          onClick: () => clearFilters(),
                        },
                      ]}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          paddingBottom: 20,
                        }}
                      >
                        <SFButtonGroup
                          items={[
                            {
                              id: LPUrls.getMyProgressUrl(
                                "jobs-and-skills/my-skills"
                              ),
                              label: t(
                                "dashboardScreen.jobsAndSkills.mySkills"
                              ),
                            },
                            {
                              id: LPUrls.getMyProgressUrl(
                                "jobs-and-skills/skill-comparison"
                              ),
                              label: t(
                                "dashboardScreen.jobsAndSkills.skillComparison"
                              ),
                            },
                            {
                              id: LPUrls.getMyProgressUrl(
                                "jobs-and-skills/time-investment"
                              ),
                              label: t(
                                "dashboardScreen.jobsAndSkills.timeInvestment"
                              ),
                            },
                          ]}
                          onClick={(item) => navigate(item.id)}
                          value={location.pathname}
                        />
                      </div>
                      <Routes>
                        <Route
                          path={PATHS.MY_SKILLS}
                          element={
                            <MySkills
                              skills={skills}
                              clearFilters={clearFilters}
                            />
                          }
                        />
                        <Route
                          path={PATHS.SKILL_COMPARISON}
                          element={<SkillComparison skills={skills} />}
                        />
                        <Route
                          path={PATHS.TIME_INVESTMENT}
                          element={<TimeInvestment items={timeInvestment} />}
                        />
                      </Routes>
                    </SFCard>
                  </SFGridItem>
                  <SFGridItem xs={12} sm={12} md={4}>
                    <SFCard
                      title={t("dashboardScreen.jobsOfInterest")}
                      actions={[
                        {
                          label: t("dashboardScreen.manageJobsOfInterest"),
                          onClick: () => {
                            dispatch(
                              profileActions.setState({
                                activeStep: PROFILE_STEPS.JOB_INTEREST,
                              })
                            );
                            navigate(getProfileUrl());
                          },
                        },
                      ]}
                    >
                      <JobsOfInterest items={jobsOfInterest} />
                    </SFCard>
                  </SFGridItem>
                </SFGridContainer>
              </>
            }
          />
        </Routes>
      </SFContentContainer>
    </>
  );
};

export default MyProgressScreen;
